import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Truck, Banknote, CheckCheck } from "lucide-react";
import WineCard from "@/components/WineCard";
import { featuredWines } from "@/data/wines";

const VINEYARD =
  "https://images.unsplash.com/photo-1567072629554-20e689de2400?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Home = () => {
  const featured = featuredWines();

  return (
    <div data-testid="home-page">
      {/* HERO — image background + dark serif text overlay (matches reference design) */}
      <section className="relative bg-[#F4EBD9]" data-testid="hero-section">
        <div className="relative w-full" style={{ aspectRatio: "1536 / 1024" }}>
          <img
            src="/hero-puglia.png"
            alt="Vignobles des Pouilles au coucher du soleil — bouteille Cantine di Puglia, verre, raisins et tire-bouchon"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
            fetchPriority="high"
          />

          {/* Title + subtitle + CTA buttons — dark text on the soft sky area */}
          <div className="absolute inset-0 flex flex-col justify-center px-4 sm:px-10 lg:px-20 max-w-[62%] sm:max-w-[55%]">
            <h1 className="font-heading text-[22px] leading-[1.1] sm:text-5xl lg:text-6xl xl:text-7xl text-[#1A1617] font-bold tracking-[-0.015em]">
              L&apos;authenticité<br />
              des vins des<br />
              <span className="italic text-[#C5A059]">Pouilles</span>
            </h1>
            <p className="text-[10px] sm:text-sm lg:text-base text-[#1A1617] font-normal mt-2.5 sm:mt-6 max-w-[200px] sm:max-w-md leading-[1.55] sm:leading-relaxed [text-shadow:0_1px_2px_rgba(255,246,228,0.85),0_0_10px_rgba(255,246,228,0.5)]">
              Découvrez notre sélection de vins italiens
              directement importés des Pouilles.
              Qualité, caractère et tradition.
            </p>
            <div className="flex flex-col gap-1.5 sm:gap-3 mt-3 sm:mt-7 items-start">
              <Link
                to="/vins"
                data-testid="hero-cta-catalog"
                className="inline-flex items-center gap-1.5 sm:gap-2 bg-[#5B2333] text-white px-3 sm:px-8 py-1.5 sm:py-3.5 text-[8px] sm:text-xs tracking-[0.14em] sm:tracking-[0.18em] uppercase font-medium hover:bg-[#4A1525] transition-colors whitespace-nowrap"
              >
                Découvrir nos vins
                <ArrowRight className="w-2.5 h-2.5 sm:w-4 sm:h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-comment-commander"
                className="inline-flex items-center bg-white/95 border border-[#1A1617]/15 text-[#1A1617] px-3 sm:px-8 py-1.5 sm:py-3.5 text-[8px] sm:text-xs tracking-[0.14em] sm:tracking-[0.18em] uppercase font-medium hover:bg-white transition-colors whitespace-nowrap"
              >
                Comment commander
              </Link>
            </div>
          </div>

          {/* 3 advantages overlaid at the very bottom of the hero image */}
          <div className="absolute left-0 right-0 bottom-0 pointer-events-none" data-testid="hero-advantages">
            <div className="absolute inset-x-0 bottom-0 h-10 sm:h-24 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
            <div className="relative max-w-6xl mx-auto px-2 sm:px-6 lg:px-10 pb-1.5 sm:pb-4 lg:pb-6 grid grid-cols-3 gap-1 sm:gap-6">
              {[
                { icon: Award, title: "Vins sélectionnés", text: "avec soin" },
                { icon: Truck, title: "Import direct", text: "des Pouilles" },
                { icon: Banknote, title: "Commande simple", text: "et rapide" },
              ].map((item) => (
                <div key={item.title} className="flex items-center gap-1 sm:gap-2.5 min-w-0">
                  <item.icon
                    className="w-2.5 h-2.5 sm:w-4 sm:h-4 lg:w-[18px] lg:h-[18px] text-white shrink-0 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
                    strokeWidth={1.5}
                  />
                  <div className="min-w-0">
                    <p className="text-[7px] sm:text-[11px] lg:text-xs tracking-[0.03em] sm:tracking-[0.1em] uppercase text-white font-medium leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">
                      {item.title}
                    </p>
                    <p className="text-[6px] sm:text-[10px] lg:text-[11px] text-white/85 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* NOTRE SÉLECTION — light section */}
      <section className="py-16 sm:py-20 md:py-28 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">Notre sélection</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-[#1A1617] mt-2">
              Nos <em className="italic text-[#5B2333]">vins</em>
            </h2>
            <span className="block w-12 h-px bg-[#C5A059] mx-auto mt-5" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
            {featured.slice(0, 8).map((w, i) => (
              <motion.div
                key={w.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-50px" }}
                custom={i}
              >
                <WineCard wine={w} />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-14">
            <Link
              to="/vins"
              data-testid="see-all-wines-link"
              className="inline-flex items-center gap-2 bg-[#5B2333] text-white px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#4A1525] transition-colors"
            >
              Voir tous nos vins
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* POURQUOI CASA VINO */}
      <section className="bg-[#0E0808] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2">
          <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[480px] overflow-hidden">
            <img src={VINEYARD} alt="Vignobles des Pouilles" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0E0808]/40" />
          </div>
          <div className="px-6 sm:px-10 lg:px-14 py-14 lg:py-20 flex flex-col justify-center">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">Notre engagement</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 leading-tight">
              Pourquoi choisir <em className="italic text-[#C5A059]">Casa Vino</em> ?
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Des vins italiens authentiques sélectionnés avec passion",
                "Un rapport qualité-prix exceptionnel",
                "Un service client réactif et à votre écoute",
                "Livraison rapide à Bruxelles et ses environs",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCheck className="w-5 h-5 text-[#C5A059] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-sm sm:text-base text-[#F9F6F0]/90 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
